


























































































































































































































import { CallApi } from '@/api/call';
import { Forbidden } from '@/api/responses/forbidden';
import assignCallDialog from '@/components/call-management/assign-call-dialog.vue';
import callsTable from '@/components/call-management/calls-table.vue';
import loading from '@/components/loading.vue';
import layout from '@/layouts/app.vue';
import { Call } from '@/shared/call';
import { PartnerPractice } from '@/shared/partner-practice';
import { DateTime } from 'luxon';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const partnerPracticesModule = namespace('partnerPractices');
const callApi = new CallApi();

const AM = 'AM';
const PM = 'PM';

@Component({
    components: {
        assignCallDialog,
        callsTable,
        layout,
        loading,
    },
})
export default class ManageCallList extends Vue {
    @partnerPracticesModule.Getter('partnerPracticesForCurrentUser') private partnerPractices!: Array<PartnerPractice>;

    private isLoading: boolean = false;
    private currentPage: number = 1;
    private pageSize: number = 50;
    private total: number = 0;
    private calls: Array<Call> = new Array<Call>();
    private partnerPracticeFilter: string | null = null;
    private dueDateFilter: string | Date | null = new Date();
    private originalSourceFilter: string | null = null;
    private callTypeFilter: number | null = null;
    private assignedStatusFilter: boolean | null = null;
    private clientFirstNameFilter: string | null = null;
    private clientLastNameFilter: string | null = null;
    private lastCalledDatePeriodFilter: number | null = null;
    private lastCalledDateFromFilter: string | Date | null = null;
    private lastCalledDateToFilter: string | Date | null = null;
    private dueTimeHourFilter: number = 23;
    private dueTimeMinuteFilter: number = 45;
    private dueTimeMeridianFilter: string = PM;
    private dueTimeUseTwentyFourHours: boolean = false;
    private assignedUserFilter: string | null = null;
    private selectedCalls: Array<Call & { selected: boolean }> = new Array<Call & { selected: boolean }>();
    private selectedPartnerPractice: PartnerPractice | null = null;
    private assignCallDialogVisible: boolean = false;
    private unassignCallConfirmationVisible: boolean = false;

    private datePeriods = new Array<{ id: number, value: string }>(...[
        { id: 0, value: 'Current Day' },
        { id: 1, value: 'Current Week' },
        { id: 2, value: 'Previous Week' },
        { id: 3, value: 'Current Month' },
        { id: 4, value: 'Previous Month' },
        { id: 5, value: 'Current Quarter' },
        { id: 6, value: 'Previous Quarter' },
        { id: 7, value: 'Current Year' },
        { id: 8, value: 'Previous Year' },
        { id: 9, value: 'Custom Dates' },
    ]);

    private get isCustomLastCalledDatePeriodFilter(): boolean {
        return this.lastCalledDatePeriodFilter === 9;
    }

    public get hours(): Array<{ id: number, value: string }> {
        const result: Array<{ id: number, value: string }> = [];

        for (let i = 0; i < (this.dueTimeUseTwentyFourHours ? 24 : 12); i++) {
            let id = i;
            let value: string = id.toString();

            if (!this.dueTimeUseTwentyFourHours) {
                id = i + 1;
                value = id.toString();

                if (this.dueTimeMeridianFilter === AM) {
                    if (id === 12) {
                        id = 0;
                    }
                } else {
                    if (id !== 12) {
                        id += 12;
                    }
                }
            }

            result.push({ id, value: value.padStart(2, '0') });
        }

        return result;
    }

    private get minutes(): Array<{ id: number, value: string }> {
        return [
            { id: 0, value: '00' },
            { id: 15, value: '15' },
            { id: 30, value: '30' },
            { id: 45, value: '45' },
        ];
    }

    @Watch('dueTimeUseTwentyFourHours')
    private onDueTimeUseTwentyFourHoursChanged(): void {
        this.dueTimeMeridianFilter = this.dueTimeHourFilter >= 12 ? PM : AM;
    }

    private async fetchCallsAsync(): Promise<void> {
        if (!await this.$validator.validateAll()) { return; }

        try {
            this.isLoading = true;

            const selectedDueDate = DateTime.fromJSDate(new Date(this.dueDateFilter!))
                .set({ hour: this.dueTimeHourFilter!, minute: this.dueTimeMinuteFilter! })
                .toUTC()
                .toJSDate();

            const result = await callApi.getCallsAsync({
                page: this.currentPage,
                pageSize: this.pageSize,
                partnerPracticeId: this.partnerPracticeFilter,
                dueDate: selectedDueDate,
                originalSourceId: this.originalSourceFilter,
                callType: this.callTypeFilter,
                assignedStatus: this.assignedStatusFilter,
                clientFirstName: this.clientFirstNameFilter,
                clientLastName: this.clientLastNameFilter,
                lastInteractionDatePeriod: this.lastCalledDatePeriodFilter,
                lastInteractionDateFrom: this.lastCalledDateFromFilter,
                lastInteractionDateTo: this.lastCalledDateToFilter,
                assignedUserId: this.assignedUserFilter,
            });

            this.calls = result.calls;
            this.total = result.total;
        } catch (e) {
            if (e instanceof (Forbidden)) {
                this.$router.push({ name: `forbidden` });
            } else if (e instanceof (Error)) {
                this.$snackbar.open({ message: e.message, type: 'is-danger' });
            } else {
                this.$snackbar.open({ message: `Could not load calls.`, type: 'is-danger' });
            }
        }

        this.isLoading = false;
    }

    private async onPageChangedAsync(value: number): Promise<void> {
        this.currentPage = value;
        await this.fetchCallsAsync();
    }

    private onAssignCalls(calls: Array<Call & { selected: boolean }>): void {
        this.selectedCalls = calls;
        this.assignCallDialogVisible = true;
    }

    private onUnassignCalls(calls: Array<Call & { selected: boolean }>): void {
        this.selectedCalls = calls;
        this.unassignCallConfirmationVisible = true;
    }

    private async onCallsAssignedAsync(): Promise<void> {
        this.$snackbar.open({ message: `Calls successfully assigned.`, type: 'is-success' });

        this.onCancel();
        await this.fetchCallsAsync();
    }

    private async onCallsUnassignedAsync(): Promise<void> {
        try {
            await callApi.unassignCallsAsync({ calls: this.selectedCalls.map((call) => call.id!) });
            this.$snackbar.open({ message: `Calls successfully un-assigned.`, type: 'is-success' });
        } catch (e) {
            if (e instanceof Forbidden) {
                this.$router.push({ name: `forbidden` });
            } else if (e instanceof Error) {
                this.$snackbar.open({ message: e.message, type: 'is-danger' });
            } else {
                this.$snackbar.open({ message: `Could not un-assign calls, please try again.`, type: 'is-danger' });
            }
        }

        this.onCancel();
        await this.fetchCallsAsync();
    }

    private onClearFilters(): void {
        this.partnerPracticeFilter = null;
        this.dueDateFilter = new Date();
        this.originalSourceFilter = null;
        this.callTypeFilter = null;
        this.assignedStatusFilter = null;
        this.clientFirstNameFilter = null;
        this.clientLastNameFilter = null;
        this.lastCalledDatePeriodFilter = null;
        this.lastCalledDateFromFilter = null;
        this.lastCalledDateToFilter = null;
        this.dueTimeHourFilter = 23;
        this.dueTimeMinuteFilter = 45;
        this.dueTimeMeridianFilter = PM;
        this.assignedUserFilter = null;
    }

    private onCancel(): void {
        this.assignCallDialogVisible = false;
        this.unassignCallConfirmationVisible = false;
        this.selectedCalls = [];
    }

    private onDueTimeMeridianFilterChanged(value: string | null): void {
        if (value === AM) {
            this.dueTimeHourFilter = this.dueTimeHourFilter === 12 ? 0 : this.dueTimeHourFilter - 12;
        } else {
            this.dueTimeHourFilter = this.dueTimeHourFilter === 0 ? 12 : this.dueTimeHourFilter + 12;
        }
    }

    private async mounted(): Promise<void> {
        this.isLoading = true;

        await Promise.all([
            this.$store.dispatch(`partnerPractices/loadPartnerPracticesAsync`),
            this.$store.dispatch(`types/loadTypesAsync`),
            this.$store.dispatch(`users/loadAllUsersAsync`),
        ]);

        this.isLoading = false;
    }
}
